import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, DollarSign, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, resolveMedia, getEffectiveRole, roleHome } from "@/lib/site";
import { useAuth } from "@/lib/auth";

export default function CreatorProfile() {
  const { handle } = useParams(); // includes the leading "@"
  const { user } = useAuth();
  const isHandle = (handle || "").startsWith("@");
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [amount, setAmount] = useState(10);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    if (!isHandle) { setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/creators/${handle}`);
        setC(data.creator); setFollowing(data.is_following);
      } catch { toast.error("Creator not found"); }
      finally { setLoading(false); }
    })();
  }, [handle]);

  const follow = async () => {
    if (!user) return (window.location.href = "/signup?type=fan");
    await api.post(`/fan/follow/${c.id}`); setFollowing(true); toast.success("Following");
  };

  const support = async (kind) => {
    if (!user) return (window.location.href = "/signup?type=fan");
    setPurchasing(true);
    try {
      const { data } = await api.post("/purchase/checkout", {
        creator_id: c.id, amount, kind, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch { toast.error("Could not start checkout"); setPurchasing(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-brand" /></div>;
  if (!isHandle) return (
    <div className="min-h-screen grid place-items-center px-6" data-testid="page-not-found">
      <div className="text-center">
        <h1 className="font-display text-4xl text-white mb-3">Page not found</h1>
        <Link to="/" className="text-brand font-semibold">Back to home</Link>
      </div>
    </div>
  );
  if (!c) return null;

  const backTo = roleHome(getEffectiveRole(user) || "fan");
  return (
    <div className="pt-28 pb-20 max-w-6xl mx-auto px-6" data-testid="creator-profile">
      <Link to={backTo} className="text-white/50 hover:text-white text-sm inline-flex items-center gap-1 mb-6"><ArrowLeft size={14} /> {backTo === "/discover" ? "Back to Discover" : "Back"}</Link>
      <div className="grid md:grid-cols-3 gap-8">
        <div>
          <div className="card overflow-hidden aspect-[4/5]">
            <img src={resolveMedia(c.avatar || "/images/leather-hero.jpg")} alt={c.name} className="w-full h-full object-cover object-top" />
          </div>
        </div>
        <div className="md:col-span-2">
          <h1 className="font-display text-4xl md:text-5xl text-white">{c.name}</h1>
          <p className="text-white/50 mt-1">{c.handle}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            {(c.interests || []).map((i) => (
              <span key={i} className="text-xs px-3 py-1 rounded-full bg-white/5 text-white/70 uppercase tracking-wide">{i}</span>
            ))}
          </div>
          <p className="mt-6 text-white/70 leading-relaxed">{c.bio || "Premium creator on Fan Focus. Follow to stay updated with drops, exclusive content, and behind the scenes."}</p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button onClick={follow} disabled={following} data-testid="profile-follow"
              className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 ${following ? "bg-white/10 text-white/70" : "bg-brand text-white hover:opacity-90"}`}>
              <Heart size={16} fill={following?"currentColor":"none"} /> {following ? "Following" : "Follow"}
            </button>
          </div>

          {/* Support */}
          <div className="card p-6 mt-8" data-testid="profile-support">
            <h3 className="font-display text-2xl text-white">Support {c.name.split(" ")[0]}</h3>
            <p className="text-white/60 text-sm mt-1">Tip or unlock exclusive content. Creator keeps 70%.</p>
            <div className="mt-4 flex gap-2 flex-wrap">
              {[5, 10, 25, 50].map(v => (
                <button key={v} onClick={()=>setAmount(v)} data-testid={`amt-${v}`}
                  className={`px-4 py-2 rounded-lg font-semibold text-sm ${amount===v ? "bg-brand text-white" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>${v}</button>
              ))}
              <input type="number" min="1" value={amount} onChange={(e)=>setAmount(Number(e.target.value)||1)} data-testid="amt-custom"
                className="w-24 bg-[#0f0819] border border-white/15 rounded-lg px-3 text-white" />
            </div>
            <div className="mt-4 flex gap-3">
              <button onClick={()=>support("tip")} disabled={purchasing} data-testid="btn-tip"
                className="flex-1 bg-brand text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
                {purchasing ? <Loader2 size={16} className="animate-spin" /> : <><DollarSign size={15} /> Send Tip</>}
              </button>
              <button onClick={()=>support("unlock")} disabled={purchasing} data-testid="btn-unlock"
                className="flex-1 border border-white/25 text-white py-3 rounded-lg font-semibold hover:border-brand hover:text-brand transition-colors">
                Unlock Content
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
